import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FileText } from 'lucide-react';
import axios from 'axios';
import { useTranslations } from '../context/TranslationsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DocumentPage = () => {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const { td: tCms } = useTranslations(['common', 'errors']);
  const lang = i18n.language;
  
  // Translation helper
  const t = (key, fallback) => {
    const cmsValue = tCms(key);
    return (cmsValue && cmsValue !== key) ? cmsValue : fallback;
  };
  
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDocument();
  }, [slug, lang]);

  const fetchDocument = async () => {
    try {
      // Pass language parameter to API for multilingual slug support
      const response = await axios.get(`${API}/documents/${slug}`, {
        params: { lang }
      });
      setDocument(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch document:', err);
      setError(err.response?.status === 404 ? 'not_found' : 'error');
    } finally {
      setLoading(false);
    }
  };

  // Simple Markdown to HTML converter
  const renderMarkdown = (text) => {
    if (!text) return '';
    return text
      .replace(/^### (.*$)/gim, '<h3 class="text-xl font-bold mt-8 mb-3 text-foreground">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-2xl font-bold mt-10 mb-4 text-foreground">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-3xl font-bold mt-10 mb-6 text-foreground">$1</h1>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^\- (.*$)/gim, '<li class="ml-6 list-disc text-muted-foreground">$1</li>')
      .replace(/^\d+\. (.*$)/gim, '<li class="ml-6 list-decimal text-muted-foreground">$1</li>')
      .replace(/\n\n/g, '</p><p class="mb-4 text-muted-foreground leading-relaxed">')
      .replace(/\n/g, '<br/>');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Ładowanie...</p>
        </div>
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-6" />
          <h1 className="text-3xl font-bold mb-4">
            {lang === 'pl' ? 'Dokument nie znaleziony' : 'Document not found'}
          </h1>
          <p className="text-muted-foreground mb-8">
            {lang === 'pl' 
              ? 'Szukany dokument nie istnieje lub został usunięty.'
              : 'The requested document does not exist or has been removed.'}
          </p>
          <Link to="/" className="btn-primary">
            {lang === 'pl' ? 'Wróć do strony głównej' : 'Back to homepage'}
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl font-bold mb-4">
            {lang === 'pl' ? 'Błąd ładowania' : 'Loading error'}
          </h1>
          <p className="text-muted-foreground mb-8">
            {lang === 'pl' 
              ? 'Nie udało się załadować dokumentu. Spróbuj ponownie później.'
              : 'Failed to load document. Please try again later.'}
          </p>
          <button onClick={fetchDocument} className="btn-primary">
            {lang === 'pl' ? 'Spróbuj ponownie' : 'Try again'}
          </button>
        </div>
      </div>
    );
  }

  const title = document?.title?.[lang] || document?.title?.pl || '';
  const content = document?.content?.[lang] || document?.content?.pl || '';

  return (
    <div className="min-h-screen pt-32 pb-20 px-6 md:px-12" data-testid="document-page">
      <div className="max-w-3xl mx-auto">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          {lang === 'pl' ? 'Powrót' : 'Back'}
        </Link>

        {/* Document Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-['Syne'] font-bold mb-4">
            {title}
          </h1>
          {document?.updatedAt && (
            <p className="text-sm text-muted-foreground">
              {lang === 'pl' ? 'Ostatnia aktualizacja: ' : 'Last updated: '}
              {new Date(document.updatedAt).toLocaleDateString(lang === 'pl' ? 'pl-PL' : 'en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          )}
        </div>

        {/* Document Content */}
        <div className="glass-card p-8 md:p-12">
          <div 
            className="prose prose-lg max-w-none dark:prose-invert"
            dangerouslySetInnerHTML={{ 
              __html: `<p class="mb-4 text-muted-foreground leading-relaxed">${renderMarkdown(content)}</p>` 
            }}
          />
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-white/10 text-center">
          <p className="text-sm text-muted-foreground">
            {lang === 'pl' 
              ? 'Masz pytania dotyczące tego dokumentu?'
              : 'Have questions about this document?'}
          </p>
          <Link to="/contact" className="text-primary hover:underline">
            {lang === 'pl' ? 'Skontaktuj się z nami' : 'Contact us'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DocumentPage;
